import { Checkbox } from '@/components/ui/checkbox';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { formatPrice, roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { useCallback, useEffect, useMemo } from 'react';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useConvertPrice } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import { BenefitTranslation } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';

type CarRentalElements = Omit<
  Required<BenefitFormData>['carRental'],
  'carCategories' | 'shouldLimitInsurance' | 'displayGlobalForm'
>;

export type CarRentalElementType = keyof CarRentalElements;

type CarRentalTranslation<T extends CarRentalElementType> = Required<
  Required<NonNullable<BenefitTranslation>>['carRental']
>[T][number];

type CarRentalElement<T extends CarRentalElementType> =
  Required<CarRentalElements>[T][number];

interface EstimateCarRentalElementProps<T extends CarRentalElementType> {
  elementType: T;
  data: CarRentalElements[T];
  selectedOptions: Record<string, SelectedEstimateOption>;
  setSelectedOptions: React.Dispatch<
    React.SetStateAction<Record<string, SelectedEstimateOption>>
  >;
  nbDays: number;
  nbCars: number;
  stepId: string;
  benefitId: string;
  carCategoryId: string;
  isMainCarCategory?: boolean;
  canCheckOnlyOneElement: boolean;
  originalCurrency?: string;
  currency?: string;
  translations?: CarRentalTranslation<T>[];
  language?: SupportedLocale;
}

const EstimateCarRentalElement = <T extends CarRentalElementType>({
  elementType,
  data,
  selectedOptions,
  setSelectedOptions,
  stepId,
  benefitId,
  carCategoryId,
  nbDays,
  nbCars,
  isMainCarCategory = false,
  canCheckOnlyOneElement,
  originalCurrency,
  currency = 'EUR',
  translations,
  language,
}: EstimateCarRentalElementProps<T>) => {
  const { t } = useTranslation();
  const { convertPrice } = useConvertPrice();

  const getTranslatedElement = useCallback(
    (index: number) => {
      return translations && language ? translations[index] : undefined;
    },
    [translations, language],
  );

  const convertIfOriginalCurrencyProvided = useCallback(
    (value: number | null | undefined) => {
      if (!value) return undefined;
      if (!originalCurrency) return value;
      return roundUpPrice(
        convertPrice(value, originalCurrency, currency),
        true,
      );
    },
    [convertPrice, currency, originalCurrency],
  );

  const title = useMemo(() => {
    switch (elementType) {
      case 'insurances':
        return t('estimates.details.carRentalElements.insurances');
      case 'options':
        return t('estimates.details.carRentalElements.options');
      case 'taxes':
        return t('estimates.details.carRentalElements.taxes');
      case 'oneWayFees':
        return t('estimates.details.carRentalElements.oneWayFees');
    }
  }, [elementType, t]);

  const isInsurance = useCallback(
    (
      _element: CarRentalElement<T>,
    ): _element is CarRentalElement<'insurances'> =>
      elementType === 'insurances',
    [elementType],
  );

  const isOption = useCallback(
    (_element: CarRentalElement<T>): _element is CarRentalElement<'options'> =>
      elementType === 'options',
    [elementType],
  );

  const isTax = useCallback(
    (_element: CarRentalElement<T>): _element is CarRentalElement<'taxes'> =>
      elementType === 'taxes',
    [elementType],
  );

  const isOneWayFee = useCallback(
    (
      _element: CarRentalElement<T>,
    ): _element is CarRentalElement<'oneWayFees'> =>
      elementType === 'oneWayFees',
    [elementType],
  );

  const getTotalAmount = useCallback(
    (element: CarRentalElement<T>) => {
      if (isOneWayFee(element))
        return (
          nbCars * (convertIfOriginalCurrencyProvided(element.amount) ?? 0)
        );
      const billingType = element.billingType ?? 'perDay';
      if (billingType === 'perDay')
        return (
          nbCars *
          nbDays *
          (convertIfOriginalCurrencyProvided(element.amount) ?? 0)
        );
      return nbCars * (convertIfOriginalCurrencyProvided(element.amount) ?? 0);
    },
    [convertIfOriginalCurrencyProvided, isOneWayFee, nbCars, nbDays],
  );

  const isSelectedCategory = useMemo(() => {
    if (
      Object.hasOwn(selectedOptions, stepId) &&
      Object.hasOwn(selectedOptions[stepId], 'carCategoryId')
    ) {
      return selectedOptions[stepId].carCategoryId === carCategoryId;
    }
    if (Object.hasOwn(selectedOptions, stepId)) {
      if (selectedOptions[stepId].benefitId !== benefitId) return false;
    }
    return isMainCarCategory;
  }, [benefitId, carCategoryId, isMainCarCategory, selectedOptions, stepId]);

  const onCheckedChange = useCallback(
    (elementId: string, elementDeltaPrice: number) => (checked: boolean) => {
      setSelectedOptions((prev) => ({
        ...prev,
        [stepId]: {
          ...prev[stepId],
          type: BenefitType.VEHICLE,
          deltaPrice: prev[stepId]?.deltaPrice ?? 0,
          benefitId,
          carCategoryOptions: {
            ...prev?.[stepId]?.carCategoryOptions,
            [elementType]: canCheckOnlyOneElement
              ? checked
                ? [{ elementId, deltaPrice: elementDeltaPrice }]
                : []
              : checked
                ? [
                    ...(prev?.[stepId]?.carCategoryOptions?.[elementType] ??
                      []),
                    { elementId, deltaPrice: elementDeltaPrice },
                  ]
                : (
                    prev?.[stepId]?.carCategoryOptions?.[elementType] ?? []
                  ).filter(({ elementId: id }) => id !== elementId),
          },
        },
      }));
    },
    [
      benefitId,
      canCheckOnlyOneElement,
      elementType,
      setSelectedOptions,
      stepId,
    ],
  );

  const isRequiredElement = useCallback(
    (element: CarRentalElement<T>) =>
      isOneWayFee(element) || (isTax(element) && element.required === true),
    [isOneWayFee, isTax],
  );

  const isElementCheckedByDefault = useCallback(
    (element: CarRentalElement<T>) =>
      selectedOptions[stepId]?.carCategoryOptions?.[elementType]?.some(
        ({ elementId }) => elementId === element.id,
      ) ?? false,
    [elementType, selectedOptions, stepId],
  );

  useEffect(() => {
    if (isSelectedCategory) {
      data
        ?.filter(
          (element) =>
            isRequiredElement(element) && !isElementCheckedByDefault(element),
        )
        .forEach((element) =>
          setSelectedOptions((prev) => ({
            ...prev,
            [stepId]: {
              ...prev[stepId],
              type: BenefitType.VEHICLE,
              deltaPrice: prev[stepId]?.deltaPrice ?? 0,
              benefitId,
              carCategoryId,
              carCategoryOptions: {
                ...prev?.[stepId]?.carCategoryOptions,
                [elementType]: [
                  ...(
                    prev?.[stepId]?.carCategoryOptions?.[elementType] ?? []
                  ).filter(({ elementId }) => element.id !== elementId),
                  {
                    elementId: element.id,
                    deltaPrice: getTotalAmount(element),
                    isRequired: true,
                  },
                ],
              },
            },
          })),
        );
    }
  }, [
    benefitId,
    data,
    getTotalAmount,
    isSelectedCategory,
    isElementCheckedByDefault,
    isTax,
    isRequiredElement,
    setSelectedOptions,
    stepId,
    elementType,
    carCategoryId,
  ]);

  if (!data || !data.length) return null;

  return (
    <div className="flex flex-col gap-2">
      <h1>{title}</h1>
      <div className="flex flex-col gap-4 text-sm font-normal text-gray-800">
        {data.map((element, indexElement) => {
          const translated = getTranslatedElement(indexElement);
          const displayName = translated?.name ?? element.name;
          const displayShortDescription =
            (isInsurance(element) &&
            translated &&
            'shortDescription' in translated
              ? translated.shortDescription
              : undefined) ??
            (isInsurance(element) ? element.shortDescription : undefined);
          const displayLongDescription =
            (isInsurance(element) &&
            translated &&
            'longDescription' in translated
              ? translated.longDescription
              : undefined) ??
            (isInsurance(element) ? element.longDescription : undefined);
          const displayDescription =
            ((isOption(element) || isOneWayFee(element)) &&
            translated &&
            'description' in translated
              ? translated.description
              : undefined) ??
            (isOption(element) || isOneWayFee(element)
              ? element.description
              : undefined);

          return (
            <div
              className="flex flex-col gap-1"
              key={`estimate-car-rental-${stepId}-${benefitId}-${elementType}-${indexElement}`}
            >
              <div className="flex flex-row gap-4 items-center">
                {isRequiredElement(element) ? (
                  <Checkbox
                    checked={
                      isSelectedCategory && isElementCheckedByDefault(element)
                    }
                    disabled
                  />
                ) : (
                  <Checkbox
                    checked={
                      isSelectedCategory && isElementCheckedByDefault(element)
                    }
                    disabled={!isSelectedCategory}
                    onCheckedChange={onCheckedChange(
                      element.id,
                      getTotalAmount(element),
                    )}
                  />
                )}
                <span>{displayName}</span>
                <span className="font-semibold text-brand-700 whitespace-nowrap">
                  + {formatPrice(getTotalAmount(element))}{' '}
                  {formatCurrency(currency)}
                </span>
              </div>
              {isInsurance(element) && (
                <>
                  {displayShortDescription && (
                    <span className="whitespace-pre-wrap">
                      {displayShortDescription}
                    </span>
                  )}
                  {displayLongDescription && (
                    <span className="whitespace-pre-wrap">
                      {displayLongDescription}
                    </span>
                  )}
                </>
              )}
              {isOption(element) && displayDescription && (
                <span className="whitespace-pre-wrap">
                  {displayDescription}
                </span>
              )}
              {isOneWayFee(element) && (
                <>
                  {element.departureCity && (
                    <span>
                      {t('estimates.details.carRentalElements.departure')}:{' '}
                      {element.departureCity}
                    </span>
                  )}
                  {element.arrivalCity && (
                    <span>
                      {t('estimates.details.carRentalElements.arrival')}:{' '}
                      {element.arrivalCity}
                    </span>
                  )}
                  {displayDescription && (
                    <span className="whitespace-pre-wrap">
                      {displayDescription}
                    </span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EstimateCarRentalElement;
